import { API_CONFIG } from './api.config';
import { Activity } from '../types/activity';
import { User, Creator, AuthResponse } from '../types/user';
import { Participation } from '../types/participation';
import { ActivityFormData } from '../types/activity';
import { AuthProviderInfo, LoginDto, SignupDto, UpdateUserDto } from '../types/auth';
import { ActivityFiltersDto } from '../types/activity-filters';
import { Application, ApplicationStatus, CreateApplicationDto } from '../types/application';
import { LocationSearchResponse, LocationSearchOptions, Location } from '../types/location';
import { LocationCoordinates, LocationValidationResponse, LocationFeature } from '../types/location';
import { CreateInvitationCodeDto, InvitationAnalytics, InvitationCode, UpdateInvitationCodeDto } from '../types/invitation-code';


class ApiService {
  private baseUrl: string;
  private defaultHeaders: HeadersInit;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.defaultHeaders = API_CONFIG.HEADERS;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
        const fullUrl = `${this.baseUrl}${endpoint}`;
        const mergedHeaders = {
            ...this.defaultHeaders,
            ...options.headers
        };
        
        // console.log('🌐 API Request:', {
        //     url: fullUrl,
        //     method: options.method || 'GET',
        //     headers: mergedHeaders,
        //     credentials: 'include',
        //     hasBody: !!options.body
        // });

        const response = await fetch(fullUrl, {
            ...options,
            headers: mergedHeaders,
            credentials: 'include',
        });

        // console.log('📡 API Response:', {
        //     status: response.status,
        //     statusText: response.statusText,
        //     ok: response.ok,
        //     headers: Object.fromEntries(response.headers.entries())
        // });

        if (!response.ok) {
          if (response.status === 401) {
            // console.log('🔐 Unauthorized response received');
            throw new Error('Unauthorized');
          }
          
          const responseData = await response.json();
          // console.log('🔍 Error message:', responseData);
          const errorMessage = responseData.message;
          
          throw new Error(errorMessage);
        }
        const data = await response.json();
        // console.log('📦 API Response data:', data);
        return data as T;
    } catch (error) {
        // console.error('💥 Error fetching data from API:', error);
        throw error;
    }
  }

  async getAllActivities(): Promise<Activity[]> {
    const endpoint = API_CONFIG.ENDPOINTS.ACTIVITIES;
    return this.request<Activity[]>(endpoint);
  }

  async getActivityById(id: string): Promise<Activity> {
    const endpoint = `${API_CONFIG.ENDPOINTS.ACTIVITIES}/${id}`;
    return this.request<Activity>(endpoint);
  }

  async getActivityTypes(): Promise<{types: string[]}> {
    const endpoint = `${API_CONFIG.ENDPOINTS.ACTIVITIES}/types`
    return this.request<{types: string[]}>(endpoint);
  }

  async getActivitiesExcludingUserJoined({userId, userLocation: {lat, lng}}: ActivityFiltersDto): Promise<Activity[]> {
    const endpoint = `${API_CONFIG.ENDPOINTS.ACTIVITIES}/unjoined-activities?userId=${userId}&lat=${lat}&lng=${lng}`;
    return this.request<Activity[]>(endpoint);
  }

  async createActivity(activity: ActivityFormData, creatorId: string): Promise<Activity> {
    const endpoint = API_CONFIG.ENDPOINTS.ACTIVITIES;
    const activityWithCreator = {
      ...activity,
      creatorId
    };
    return this.request<Activity>(endpoint, {
        method: 'POST',
        body: JSON.stringify(activityWithCreator),
    });
  }

  async updateActivity(activityId: string, activity: ActivityFormData): Promise<Activity> {
    const endpoint = `${API_CONFIG.ENDPOINTS.ACTIVITIES}/${activityId}`;
    return this.request<Activity>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(activity),
    });
  }

  async deleteActivity(activityId: string): Promise<{message: string}> {
    const endpoint = `${API_CONFIG.ENDPOINTS.ACTIVITIES}/${activityId}`;
    return this.request<{message: string}>(endpoint, {
      method: 'DELETE',
    });
  }

  async quitActivity(activityId: string): Promise<{message: string}> {
    const endpoint = `${API_CONFIG.ENDPOINTS.PARTICIPATIONS}/quit/${activityId}`;
    return this.request<{message: string}>(endpoint, {
        method: 'PATCH'
    })
  }

  async login(loginDto: LoginDto): Promise<User> {
    const endpoint = `${API_CONFIG.ENDPOINTS.AUTH}/login`
    return this.request<User>(endpoint, {
        method: 'POST',
        body: JSON.stringify(loginDto)
    })
  }

  async logout(): Promise<{message: string}> {
    const endpoint = `${API_CONFIG.ENDPOINTS.AUTH}/logout`
    return this.request<{message: string}>(endpoint, {
        method: 'POST',
    })
  }

  async signup(signupDto: SignupDto): Promise<User> {
    const endpoint = `${API_CONFIG.ENDPOINTS.AUTH}/signup`
    return this.request<User>(endpoint, {
        method: 'POST',
        body: JSON.stringify(signupDto)
    })
  }

  async loginWithGoogle(): Promise<void> {
    const googleAuthEndpoint = `${API_CONFIG.ENDPOINTS.AUTH}/google-login`;
    const authUrl = `${this.baseUrl}${googleAuthEndpoint}`;
    // console.log('initiateGoogleAuth', authUrl);
    // full page redirect, so no request/fetch
    window.location.href = authUrl;
  }

  async signupWithGoogle(): Promise<void> {
    const googleAuthEndpoint = `${API_CONFIG.ENDPOINTS.AUTH}/google-signup`;
    const authUrl = `${this.baseUrl}${googleAuthEndpoint}`;
    // console.log('initiateGoogleAuth', authUrl);
    // full page redirect, so no request/fetch
    window.location.href = authUrl;
  }

  async getUserProviderInfo(): Promise<AuthProviderInfo> {
    const endpoint = `${API_CONFIG.ENDPOINTS.AUTH}/provider-info`;
    return this.request<AuthProviderInfo>(endpoint, {
      method: 'GET',
    })
  }

  async updateUser(updateUserDto: UpdateUserDto): Promise<User> {
    const endpoint = `${API_CONFIG.ENDPOINTS.AUTH}/update`
    return this.request<User>(endpoint, {
      method: 'POST',
      body: JSON.stringify(updateUserDto)
    })
  }

  async getCurrentUser(): Promise<AuthResponse> {
    const endpoint = `${API_CONFIG.ENDPOINTS.AUTH}/me`
    return this.request<AuthResponse>(endpoint, {
        method: 'GET'
    })
  }

  async updateUserProfile(profile: UpdateUserDto): Promise<User> {
    const endpoint = `${API_CONFIG.ENDPOINTS.AUTH}/update`
    return this.request<User>(endpoint, {
      method: 'POST',
      body: JSON.stringify(profile)
    })
  }

  async getComingActivities(userId: string): Promise<Activity[]> {
    const endpoint = `${API_CONFIG.ENDPOINTS.PARTICIPATIONS}/coming/${userId}`
    // const userParticipations = await this.request<Participation[]>(endpoint);
    const userParticipations = await this.request<any[]>(endpoint);
    // const activityPromises = (userParticipations || []).map(participation =>
    //   this.request<Activity>(`${API_CONFIG.ENDPOINTS.ACTIVITIES}/${participation.activityId}`)
    // );
    // const joinedActivities = await Promise.all(activityPromises);
    // return joinedActivities
    return userParticipations.map(participation => participation.activity);
  }

  async getPastActivities(userId: string): Promise<Activity[]> {
    const endpoint = `${API_CONFIG.ENDPOINTS.PARTICIPATIONS}/past/${userId}`
    const userParticipations = await this.request<any[]>(endpoint);
    
    return userParticipations.map(participation => participation.activity);
  }

  async getParticipationsByActivityId(activityId: string): Promise<Participation[]> {
    const endpoint = `${API_CONFIG.ENDPOINTS.PARTICIPATIONS}/activity/${activityId}`
    return this.request<Participation[]>(endpoint);
  }

  async getParticipationByUserIdActivityId(userId: string, activityId: string): Promise<Participation> {
    const endpoint = `${API_CONFIG.ENDPOINTS.PARTICIPATIONS}/user/${userId}/activity/${activityId}`;
    return this.request<Participation>(endpoint);
  }

  async getCreatorById(id: string): Promise<Creator> {
    const endpoint = `${API_CONFIG.ENDPOINTS.AUTH}/user/${id}`
    return this.request<Creator>(endpoint);
  }

  async getUserById(id: string): Promise<User> {
    const endpoint = `${API_CONFIG.ENDPOINTS.AUTH}/user/${id}`
    return this.request<User>(endpoint);
  }


  async createApplication(application: CreateApplicationDto): Promise<Application> {
    const endpoint = `${API_CONFIG.ENDPOINTS.APPLICATIONS}`;
    return this.request<Application>(endpoint, {
      method: 'POST',
      body: JSON.stringify(application),
    })
  }

  async updateApplication(applicationId: string, status: ApplicationStatus): Promise<Application> {
    const endpoint = `${API_CONFIG.ENDPOINTS.APPLICATIONS}`;
    return this.request<Application>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify({applicationId, status}),
    })
  }

  async getApplicationsByUserId(userId: string): Promise<any[]> {
    // for the coming soon view, to show the status of the applied activities
    // the response includes the activity object so we could render the activities
    // along with the applications status
    const endpoint = `${API_CONFIG.ENDPOINTS.APPLICATIONS}/user/${userId}`;
    return this.request<any[]>(endpoint, {
      method: 'GET',
    })
  }

  async getApplicationByUserIdActivityId(userId: string, activityId: string): Promise<any> {
    // for the explore view to show and filter the applied activities
    // also for the flip card to allow user to reapply if they have withdrawn
    const endpoint = `${API_CONFIG.ENDPOINTS.APPLICATIONS}/user/${userId}/activity/${activityId}`;
    const application = await this.request<Application>(endpoint);
    return application;
  }

  async getActivitiesByCreatorId(creatorId: string): Promise<Activity[]> {
    const endpoint = `${API_CONFIG.ENDPOINTS.ACTIVITIES}/creator/${creatorId}`;
    return this.request<Activity[]>(endpoint);
  }

  async getApplicationsByActivityId(activityId: string): Promise<Application[]> {
    const endpoint = `${API_CONFIG.ENDPOINTS.APPLICATIONS}/activity/${activityId}`;
    return this.request<Application[]>(endpoint, {
      method: 'GET',
    });
  }

  async uploadAvatar(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'AVATAR');

    const endpoint = `${API_CONFIG.ENDPOINTS.IMAGES}/upload`;
    return fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      // headers: {
      //   'Content-Type': undefined,
      // } as any,
      body: formData,
      credentials: 'include',
    });
  }

  async uploadActivityImages(file: File, activityId: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'ACTIVITY_IMAGE');
    if (activityId) {
      formData.append('entityId', activityId);
      formData.append('entityType', 'activity');
    }

    const endpoint = `${API_CONFIG.ENDPOINTS.IMAGES}/upload`;
    return this.request(endpoint, {
      method: 'POST',
      body: formData,
    });
  }

  async uploadImage(file: File, type: string): Promise<any> {
    // TODO: this is kinda hacky, we should have a more structured way 
    // to handle the different types of requests header that's not application/json
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const endpoint = `${API_CONFIG.ENDPOINTS.IMAGES}/upload`;
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      // headers: {
      //   'Content-Type': undefined,
      // } as any,
      body: formData,
      credentials: 'include',
    });
    return await response.json();
  }

  async uploadImages(files: File[], type: string, entityId?: string): Promise<any> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('type', type);
    if (entityId) {
      formData.append('entityId', entityId);
      formData.append('entityType', type === 'ACTIVITY_IMAGE' ? 'activity' : 'user');
    }

    const endpoint = `${API_CONFIG.ENDPOINTS.IMAGES}/upload-batch`;
    return this.request(endpoint, {
      method: 'POST',
      body: formData,
    });
  }

  async getImages(filters?: any): Promise<any[]> {
    const params = new URLSearchParams(filters);
    const endpoint = `${API_CONFIG.ENDPOINTS.IMAGES}?${params.toString()}`;
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async deleteImage(imageId: string): Promise<any> {
    const endpoint = `${API_CONFIG.ENDPOINTS.IMAGES}/${imageId}`;
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  async searchLocations(query: string, options?: LocationSearchOptions): Promise<LocationSearchResponse> {
    const endpoint = `${API_CONFIG.ENDPOINTS.LOCATIONS}/search`;
    const params = new URLSearchParams({
      q: query,
      ...(options?.limit && { limit: options.limit.toString() }),
      ...(options?.proximity && { proximity: `${options.proximity.longitude},${options.proximity.latitude}` }),
      ...(options?.boundingBox && { bbox: options.boundingBox.join(',') }),
      ...(options?.country && { country: options.country.join(',') }),
      ...(options?.types && { types: options.types.join(',') }),
    })
    return this.request(`${endpoint}?${params.toString()}`, {
      method: 'GET',
    });
  }

  async reverseGeocode(coordinates: LocationCoordinates, types?: string[]): Promise<LocationSearchResponse> {
    const params = new URLSearchParams({
      longitude: coordinates.longitude.toString(),
      latitude: coordinates.latitude.toString(),
      ...(types && { types: types.join(',') }),
    });
    const endpoint = `${API_CONFIG.ENDPOINTS.LOCATIONS}/reverse?${params.toString()}`;
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async validateLocation(locationData: any): Promise<LocationValidationResponse> {
    const endpoint = `${API_CONFIG.ENDPOINTS.LOCATIONS}/validate`;
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(locationData),
    });
  }

  // this is typically not called directly by frontend
  // the backend handles location creation during activity creation
  async createLocationFromFeature(locationFeature: LocationFeature): Promise<string> {
    const endpoint = `${API_CONFIG.ENDPOINTS.LOCATIONS}/create-from-feature`;
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify({ locationFeature }),
    });
  }

  async getLocationById(id: string): Promise<Location | null> {
    const endpoint = `${API_CONFIG.ENDPOINTS.LOCATIONS}/${id}`;
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async findNearbyLocations(
    coordinates: LocationCoordinates,
    radiusKm: number = 10,
    limit: number = 20
  ): Promise<Location[]> {
    const params = new URLSearchParams({
      latitude: coordinates.latitude.toString(),
      longitude: coordinates.longitude.toString(),
      radius: radiusKm.toString(),
      limit: limit.toString(),
    });
    const endpoint = `${API_CONFIG.ENDPOINTS.LOCATIONS}/nearby?${params.toString()}`;
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  getCoordinatesFromLocation(location: Location): LocationCoordinates | null {
    if (location.latitude && location.longitude) {
      return {
        latitude: location.latitude,
        longitude: location.longitude,
      };
    }
    return null;
  }

  formatLocationDisplayName(location: Location): string {
    const parts = [location.name];
    if (location.city && location.city !== location.name) {
      parts.push(location.city);
    }
    if (location.state) {
      parts.push(location.state);
    }
    return parts.join(', ');
  }

  async validateInvitation(code: string): Promise<{valid: boolean, codeId: string}> {
    const endpoint = `${API_CONFIG.ENDPOINTS.INVITATIONS}/validate`;
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async createInvitationCode(createInvitationCodeDto: CreateInvitationCodeDto): Promise<InvitationCode> {
    const endpoint = `${API_CONFIG.ENDPOINTS.INVITATIONS}/create`;
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(createInvitationCodeDto),
    });
  }

  async getInvitationAnalytics(): Promise<InvitationAnalytics> {
    const endpoint = `${API_CONFIG.ENDPOINTS.INVITATIONS}/analytics`;
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async updateInvitationCode(updateInvitationCodeDto: UpdateInvitationCodeDto): Promise<InvitationCode> {
    const endpoint = `${API_CONFIG.ENDPOINTS.INVITATIONS}/update`;
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(updateInvitationCodeDto),
    });
  }

  async storeInvitationCode({invitationCodeId}: {invitationCodeId: string}): Promise<any> {
    const endpoint = `${API_CONFIG.ENDPOINTS.AUTH}/store-invitation-code`;
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify({ invitationCodeId }),
    });
  }

  // Email verification
  async resendVerificationEmail(email: string): Promise<{ message: string }> {
    const endpoint = `${API_CONFIG.ENDPOINTS.AUTH}/resend-verification`;
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }
}

export const apiService = new ApiService();
